import React, { useEffect, useState } from "react";
import axios from "axios";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { Badge } from "../../design/badge";
import { Button } from "../../design/button";
import ArtistAvatar from "../../components/ArtistAvatar";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { EmergingArtist, BadgeColor } from "../../types";
import {
  ArrowTrendingUpIcon,
  FireIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";

const EmergingArtistsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [emerging, setEmerging] = useState<EmergingArtist[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchEmergingArtists();
    }
  }, [user, categoryFilter]);

  const fetchEmergingArtists = async () => {
    setIsLoading(true);
    try {
      const token = await user?.getIdToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      const params = new URLSearchParams();
      if (categoryFilter) params.append("category", categoryFilter);
      params.append("limit", "50");

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/market_trends/emerging?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const emergingArtists = (response.data.data || []).map((artist: any) => ({
        id: artist.artistId,
        _id: artist.artistId,
        name: artist.name,
        growth: artist.growth,
        recentAvgPrice: artist.recentAvgPrice,
        recentCount: artist.recentCount,
        nationality: artist.nationality,
        birthYear: artist.birthYear,
        image_url: artist.image_url,
        medium_image_url: artist.medium_image_url,
        small_image_url: artist.small_image_url,
      }));

      setEmerging(emergingArtists);
    } catch (error: any) {
      console.error("Error fetching emerging artists:", error);
      setEmerging([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getGrowthBadgeColor = (growth: number): BadgeColor | undefined => {
    if (growth > 100) return "red";
    if (growth > 50) return "orange";
    if (growth > 30) return "yellow";
    return "lime";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text>Loading emerging artists...</Text>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
              <SparklesIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <Heading>Emerging Artists</Heading>
              <Text className="text-sm text-gray-500">
                Artists with rapid price growth and rising market demand
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button onClick={fetchEmergingArtists}>Refresh Data</Button>
            <Button outline onClick={() => navigate("/market")}>
              Back to Market Dashboard
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3">
          <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <FireIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              <Subheading>Total Artists</Subheading>
            </div>
            <Text className="text-4xl font-bold">{emerging.length}</Text>
            <Text className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Artists with 20%+ growth
            </Text>
          </div>

          <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <ArrowTrendingUpIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              <Subheading>Avg Growth</Subheading>
            </div>
            <Text className="text-4xl font-bold">
              {emerging.length > 0
                ? (
                    emerging.reduce((sum, a) => sum + a.growth, 0) /
                    emerging.length
                  ).toFixed(0)
                : 0}
              %
            </Text>
            <Text className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Year-over-year price increase
            </Text>
          </div>

          <div className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <SparklesIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <Subheading>Top Growth</Subheading>
            </div>
            <Text className="text-4xl font-bold">
              {emerging.length > 0 ? Math.max(...emerging.map((a) => a.growth)).toFixed(0) : 0}%
            </Text>
            <Text className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Highest performer
            </Text>
          </div>
        </div>

        {/* Info Box */}
        <div className="p-6 mb-8 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-950/20 dark:border-blue-900">
          <Subheading className="mb-2">What makes an artist "emerging"?</Subheading>
          <Text className="text-sm text-gray-700 dark:text-gray-300">
            Our algorithm identifies emerging artists based on recent auction market performance:
          </Text>
          <ul className="mt-3 ml-6 space-y-1 text-sm text-gray-700 list-disc dark:text-gray-300">
            <li>Recent appearance on the auction market (within last 2 years)</li>
            <li>20% or higher price growth year-over-year</li>
            <li>At least 3 recent sales for statistical significance</li>
            <li>Works priced under $100,000 (accessibility threshold)</li>
            <li>Rising demand and buyer interest</li>
          </ul>
        </div>

        {/* Artists Grid */}
        {emerging.length === 0 ? (
          <div className="py-16 text-center">
            <Text className="text-gray-500">No emerging artists data available</Text>
            <Button onClick={fetchEmergingArtists} className="mt-4">
              Try Again
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {emerging.map((artist, idx) => (
              <div
                key={idx}
                onClick={() => {
                  const artistId = artist.id || artist._id;
                  if (artistId) {
                    navigate(`/artist/${artistId}`);
                  } else {
                    navigate(`/auctionRecords?search=${encodeURIComponent(artist.name)}`);
                  }
                }}
                className="p-6 transition-all bg-white rounded-lg shadow-md cursor-pointer dark:bg-zinc-900 hover:shadow-xl hover:scale-105 group"
              >
                {/* Artist Avatar & Info */}
                <div className="flex items-start gap-4 mb-4">
                  <ArtistAvatar artist={artist} size="lg" />
                  <div className="flex-1 min-w-0">
                    <Text className="font-bold transition-colors line-clamp-1 group-hover:text-purple-600 dark:group-hover:text-purple-400">
                      {artist.name}
                    </Text>
                    {artist.nationality && (
                      <Text className="text-sm text-gray-600 dark:text-gray-400">
                        {artist.nationality}
                        {artist.birthYear && `, b. ${artist.birthYear}`}
                      </Text>
                    )}
                  </div>
                </div>

                {/* Growth Metrics */}
                <div className="flex items-center justify-between p-4 mb-3 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
                  <div>
                    <Text className="text-xs text-gray-600 dark:text-gray-400">
                      Price Growth
                    </Text>
                    <Text className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      +{artist.growth.toFixed(0)}%
                    </Text>
                  </div>
                  <Badge color={getGrowthBadgeColor(artist.growth)}>
                    {artist.growth > 100 ? "🚀 HOT" : artist.growth > 50 ? "🔥 Rising" : "📈 Growing"}
                  </Badge>
                </div>

                {/* Price & Sales Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-gray-50 dark:bg-zinc-800">
                    <Text className="text-xs text-gray-600 dark:text-gray-400">
                      Recent Avg Price
                    </Text>
                    <Text className="mt-1 font-semibold">
                      ${artist.recentAvgPrice.toLocaleString()}
                    </Text>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 dark:bg-zinc-800">
                    <Text className="text-xs text-gray-600 dark:text-gray-400">
                      Recent Sales
                    </Text>
                    <Text className="mt-1 font-semibold">
                      {artist.recentCount || 0} lots
                    </Text>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmergingArtistsPage;
